import { css } from '@emotion/react';
import { useState } from 'react';
import { getBuildInfo } from './BuildInfo';
import { UIColorPalette } from './ColorPalette';
import { graphql } from './generated/gql';
import { useGraphQLQuery } from './GraphQLHelpers';

const AdminMetricsQuery = graphql(`
  query AdminMetrics {
    developer {
      channelMetrics {
        connectionCount
        authenticatedConnectionCount
        connections {
          socketID
          user {
            id
            name
          }
          connectedAt
          lastUpdated
        }
      }
      serverEnv {
        hostname
        nodeEnv
        httpPort
        channelPort
        channelMode
      }
    }
  }
`);

export default function AdminPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const buildInfo = getBuildInfo();

  const { data } = useGraphQLQuery(AdminMetricsQuery, undefined, {
    refreshMs: autoRefresh ? 5000 : undefined,
  });
  const metrics = data?.developer?.channelMetrics;
  const serverEnv = data?.developer?.serverEnv;

  return (
    <div css={AdminStyles.container}>
      <h1 css={AdminStyles.title}>Admin Dashboard</h1>

      <div css={AdminStyles.controls}>
        <label css={AdminStyles.checkboxLabel}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (5s)
        </label>
      </div>

      <div css={AdminStyles.topRow}>
        <section css={AdminStyles.topRowSection}>
          <h2 css={AdminStyles.sectionTitle}>Build Information</h2>
          <div css={AdminStyles.infoGrid}>
            <div css={AdminStyles.infoRow}>
              <span css={AdminStyles.infoLabel}>Git Commit</span>
              <span css={AdminStyles.infoValue}>{buildInfo.gitCommit}</span>
            </div>
            <div css={AdminStyles.infoRow}>
              <span css={AdminStyles.infoLabel}>Build Time</span>
              <span css={AdminStyles.infoValue}>
                {new Date(buildInfo.buildTime).toLocaleString()}
              </span>
            </div>
          </div>
        </section>

        <section css={AdminStyles.topRowSection}>
          <h2 css={AdminStyles.sectionTitle}>Server Environment</h2>
          {!data ? (
            <p css={AdminStyles.loadingText}>Loading...</p>
          ) : !serverEnv ? (
            <p css={AdminStyles.errorText}>Failed to load server environment</p>
          ) : (
            <div css={AdminStyles.infoGrid}>
              <div css={AdminStyles.infoRow}>
                <span css={AdminStyles.infoLabel}>Hostname</span>
                <span css={AdminStyles.infoValue}>{serverEnv.hostname}</span>
              </div>
              <div css={AdminStyles.infoRow}>
                <span css={AdminStyles.infoLabel}>Node Env</span>
                <span css={AdminStyles.infoValue}>{serverEnv.nodeEnv}</span>
              </div>
              <div css={AdminStyles.infoRow}>
                <span css={AdminStyles.infoLabel}>HTTP Port</span>
                <span css={AdminStyles.infoValue}>
                  {serverEnv.httpPort || '(default)'}
                </span>
              </div>
              <div css={AdminStyles.infoRow}>
                <span css={AdminStyles.infoLabel}>Channel Port</span>
                <span css={AdminStyles.infoValue}>
                  {serverEnv.channelPort || '(default)'}
                </span>
              </div>
              <div css={AdminStyles.infoRow}>
                <span css={AdminStyles.infoLabel}>Channel Mode</span>
                <span css={AdminStyles.infoValue}>
                  {serverEnv.channelMode || '(default)'}
                </span>
              </div>
            </div>
          )}
        </section>
      </div>

      <section css={AdminStyles.section}>
        <h2 css={AdminStyles.sectionTitle}>Channel Metrics</h2>
        {!data ? (
          <p css={AdminStyles.loadingText}>Loading...</p>
        ) : !metrics ? (
          <p css={AdminStyles.errorText}>Failed to load channel metrics</p>
        ) : (
          <>
            <div css={AdminStyles.statsGrid}>
              <div css={AdminStyles.statCard}>
                <div css={AdminStyles.statValue}>{metrics.connectionCount}</div>
                <div css={AdminStyles.statLabel}>Total Connections</div>
              </div>
              <div css={AdminStyles.statCard}>
                <div css={AdminStyles.statValue}>
                  {metrics.authenticatedConnectionCount}
                </div>
                <div css={AdminStyles.statLabel}>Authenticated</div>
              </div>
            </div>

            <h3 css={AdminStyles.tableTitle}>Active Connections</h3>
            {metrics.connections.length === 0 ? (
              <p css={AdminStyles.emptyText}>No active connections</p>
            ) : (
              <table css={AdminStyles.table}>
                <thead>
                  <tr>
                    <th>Socket ID</th>
                    <th>User</th>
                    <th>Connected</th>
                    <th>Last Heartbeat</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.connections.map((conn) => (
                    <tr key={conn.socketID}>
                      <td>
                        <CopyableID value={conn.socketID} />
                      </td>
                      <td>
                        {conn.user ? (
                          <>
                            {conn.user.name} <CopyableID value={conn.user.id} />
                          </>
                        ) : (
                          '(anonymous)'
                        )}
                      </td>
                      <td>{formatDuration(Date.now() - conn.connectedAt)}</td>
                      <td>
                        {formatDuration(Date.now() - conn.lastUpdated)} ago
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function CopyableID({ value }: { value: string }) {
  const truncated = value.slice(0, 8) + '...';
  const handleClick = () => {
    navigator.clipboard.writeText(value);
  };
  return (
    <span css={AdminStyles.copyableID} onClick={handleClick} title={value}>
      {truncated}
    </span>
  );
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

const AdminStyles = {
  container: css({
    padding: 16,
    maxWidth: 900,
    margin: '0 auto',
  }),
  title: css({
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    textAlign: 'center',
  }),
  controls: css({
    marginBottom: 16,
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  }),
  checkboxLabel: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  }),
  section: css({
    marginBottom: 32,
  }),
  topRow: css({
    display: 'flex',
    gap: 16,
    marginBottom: 32,
    flexWrap: 'wrap',
  }),
  topRowSection: css({
    flex: 1,
    minWidth: 280,
  }),
  sectionTitle: css({
    fontSize: '1.5rem',
    marginBottom: 16,
    borderBottom: `1px solid ${UIColorPalette.listItemBottomBorder}`,
    paddingBottom: 8,
  }),
  loadingText: css({
    color: 'rgb(150, 150, 150)',
    fontStyle: 'italic',
  }),
  errorText: css({
    color: 'rgb(200, 100, 100)',
    fontStyle: 'italic',
  }),
  statsGrid: css({
    display: 'flex',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  }),
  statCard: css({
    backgroundColor: UIColorPalette.listBackground,
    padding: 16,
    borderRadius: 8,
    textAlign: 'center',
    minWidth: 140,
    border: `1px solid ${UIColorPalette.listItemBottomBorder}`,
  }),
  statValue: css({
    fontSize: 32,
    fontWeight: 'bold',
  }),
  statLabel: css({
    fontSize: 14,
    opacity: 0.8,
    marginTop: 4,
  }),
  tableTitle: css({
    fontSize: '1.2rem',
    marginBottom: 12,
  }),
  emptyText: css({
    color: 'rgb(150, 150, 150)',
    fontStyle: 'italic',
  }),
  table: css({
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: UIColorPalette.listBackground,
    '& th, & td': {
      padding: 10,
      textAlign: 'left',
      borderBottom: `1px solid ${UIColorPalette.listItemBottomBorder}`,
    },
    '& th': {
      fontWeight: 'bold',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
    '& tr:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  }),
  copyableID: css({
    fontFamily: 'monospace',
    fontSize: 12,
    opacity: 0.7,
    cursor: 'pointer',
    '&:hover': {
      opacity: 1,
    },
  }),
  infoGrid: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    backgroundColor: UIColorPalette.listBackground,
    padding: 16,
    borderRadius: 8,
    border: `1px solid ${UIColorPalette.listItemBottomBorder}`,
  }),
  infoRow: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  infoLabel: css({
    fontWeight: 'bold',
    opacity: 0.8,
  }),
  infoValue: css({
    fontFamily: 'monospace',
  }),
};
